import { toThousands } from '@patsnap/synapse_common_utils'
import { defineComponent, PropType } from '@vue/composition-api'
import { ElPopover } from '../../../element-ui'
import { IHiroCompanyCreditsUsage, IHiroPersonalCreditsUsage, IHiroUsageCount, IHiroUsageDetail } from '../../../composition/useHiroUsage'
import cn from './locales/cn.json'
import en from './locales/en.json'
import jp from './locales/jp.json'

const MAX_VISIBLE_EXPIRING_ITEMS = 2

const FREQUENCY_KEY = {
  DAILY: 'daily',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
} as const

export const BHiroUsageDetail = defineComponent({
  name: 'BHiroUsageDetail',
  i18n: {
    messages: {
      cn,
      en,
      jp,
    },
  },
  props: {
    usageDetail: {
      type: Object as PropType<IHiroUsageDetail>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    formatNumber(value: number) {
      return toThousands(value)
    },
    formatDate(timestamp: number) {
      const locale = this.$i18n.locale === 'cn' ? 'zh-CN' : 'en-US'
      return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
      }).format(timestamp)
    },
    formatUsage(item: IHiroUsageCount) {
      const frequencyKey = item.frequency && item.frequency !== 'ALL' ? FREQUENCY_KEY[item.frequency] : undefined
      return this.$t('BHiroUsageDetail.usedAndTotal', {
        used: this.formatNumber(item.current),
        total: this.formatNumber(item.total),
        frequency: frequencyKey ? this.$t(`BHiroUsageDetail.${frequencyKey}`) : '',
      })
    },
    renderUses() {
      if (!this.usageDetail.uses.length) {
        return null
      }

      return (
        <section>
          <h3 class="mb-6 text-xl font-semibold leading-7 text-text-t1">{this.$t('BHiroUsageDetail.uses')}</h3>
          <div class="space-y-4">
            {this.usageDetail.uses.map((item, index) => (
              <div
                class={['pb-2 text-sm leading-6 text-text-t1', index < this.usageDetail.uses.length - 1 && 'border-b border-gray-40']}
                key={item.mode}
              >
                <span>{this.$t(`BHiroUsageDetail.${item.mode}`)}: </span>
                <span>{this.formatUsage(item)}</span>
              </div>
            ))}
          </div>
        </section>
      )
    },
    renderExpiringItem(item: IHiroPersonalCreditsUsage['expiringItems'][number]) {
      return this.$t('BHiroUsageDetail.expiringItem', {
        amount: this.formatNumber(item.available),
        date: this.formatDate(item.endAt),
      })
    },
    renderPersonalCredits(credits: IHiroPersonalCreditsUsage) {
      const visibleItems = credits.expiringItems.slice(0, MAX_VISIBLE_EXPIRING_ITEMS)
      const hiddenItems = credits.expiringItems.slice(MAX_VISIBLE_EXPIRING_ITEMS)

      return (
        <div class="space-y-4 text-sm leading-6 text-text-t1">
          <div class="border-b border-gray-40 pb-2">
            <i18n path="BHiroUsageDetail.available" tag="span">
              <span place="amount" class="font-semibold">
                {this.formatNumber(credits.available)}
              </span>
            </i18n>
          </div>
          {visibleItems.length ? (
            <div class="flex items-start justify-between gap-4 pb-2">
              <span class="flex-shrink-0">{this.$t('BHiroUsageDetail.expiringSoon')}</span>
              <div class="min-w-0 space-y-1 text-right text-text-t2">
                {visibleItems.map((item) => (
                  <div key={`${item.endAt}-${item.available}`}>{this.renderExpiringItem(item)}</div>
                ))}
                {hiddenItems.length ? (
                  <ElPopover trigger="hover" placement="right" visibleArrow={false} width={220}>
                    <div class="space-y-1 text-sm leading-6 text-text-t2">
                      {hiddenItems.map((item) => (
                        <div key={`${item.endAt}-${item.available}`}>{this.renderExpiringItem(item)}</div>
                      ))}
                    </div>
                    <button slot="reference" type="button" class="cursor-pointer border-0 bg-transparent p-0 leading-6 text-blue-default underline">
                      +{hiddenItems.length}
                    </button>
                  </ElPopover>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      )
    },
    renderCompanyCredits(credits: IHiroCompanyCreditsUsage) {
      if (credits.mode === 'daily_limit') {
        return (
          <div class="pb-2 text-sm leading-6 text-text-t1">
            {this.$t('BHiroUsageDetail.usedAndTotal', {
              used: this.formatNumber(credits.used),
              total: this.formatNumber(credits.total),
              frequency: this.$t('BHiroUsageDetail.daily'),
            })}
          </div>
        )
      }

      return (
        <div class="space-y-4">
          <div class="border-b border-gray-40 pb-2 text-sm leading-6 text-text-t1">{this.$t('BHiroUsageDetail.sharedPool')}</div>
          <div class="pb-2 text-xs leading-4 text-text-t2">{this.$t('BHiroUsageDetail.sharedPoolTip')}</div>
        </div>
      )
    },
    renderCredits() {
      const credits = this.usageDetail.credits

      if (!credits) {
        return null
      }

      const content = credits.accountType === 'personal' ? this.renderPersonalCredits(credits) : this.renderCompanyCredits(credits)

      return (
        <section class={this.usageDetail.uses.length ? 'mt-10' : ''}>
          <h3 class="mb-6 text-xl font-semibold leading-7 text-text-t1">{this.$t('BHiroUsageDetail.credits')}</h3>
          {content}
          {this.usageDetail.uses.length ? <div class="mt-4 text-xs leading-4 text-text-t2">{this.$t('BHiroUsageDetail.deductionTip')}</div> : null}
        </section>
      )
    },
  },
  render() {
    const statusMessageKey = this.loading ? 'loading' : !this.usageDetail.uses.length && !this.usageDetail.credits ? 'empty' : undefined

    if (statusMessageKey) {
      return (
        <div class="flex min-h-[120px] w-full items-center justify-center p-4 text-sm text-text-t3">
          {this.$t(`BHiroUsageDetail.${statusMessageKey}`)}
        </div>
      )
    }

    return (
      <div class="w-full p-4">
        {this.renderUses()}
        {this.renderCredits()}
      </div>
    )
  },
})
